import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  FieldArray,
  FieldArrayMethodProps,
  FieldArrayPath,
  FieldPath,
  FieldValues,
  Path,
  UseFieldArrayProps,
  UseFieldArrayReturn,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { SelectGroup, SelectOption, SelectValueType } from "../control/types";
import { OptionsOrGroups } from "../select";
import { areValuesEqual, filterOptions, filterValues } from "./utils";

export function useDependantControlsTrigger<TFieldValues extends FieldValues>(
  value: any,
  controls: FieldPath<TFieldValues> | FieldPath<TFieldValues>[],
) {
  const { trigger } = useFormContext<TFieldValues>();
  const previousValue = useRef(value);

  useEffect(() => {
    if (previousValue.current !== value) {
      trigger(controls);
    }

    previousValue.current = value;
  }, [value, controls, trigger]);
}

export function useParentControlsTrigger<TFieldValues extends FieldValues>(
  name: FieldPath<TFieldValues> | FieldPath<TFieldValues>[],
  controls: FieldPath<TFieldValues>[],
) {
  const { trigger, watch } = useFormContext<TFieldValues>();

  useEffect(() => {
    const { unsubscribe } = watch((_, info) => {
      if (info.name && controls.includes(info.name)) {
        trigger(name);
      }
    });

    return () => unsubscribe();
  }, [name, controls, trigger]);
}

export function useFormArray<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> =
    FieldArrayPath<TFieldValues>,
  TKeyName extends string = "id",
>(
  props: UseFieldArrayProps<TFieldValues, TFieldArrayName, TKeyName> & {
    parentControlNames?: FieldPath<TFieldValues> | FieldPath<TFieldValues>[];
  },
): UseFieldArrayReturn<TFieldValues, TFieldArrayName, TKeyName> {
  const { parentControlNames, ...fieldProps } = props;

  const { trigger } = useFormContext<TFieldValues>();
  const { append, remove, ...returnProps } = useFieldArray<
    TFieldValues,
    TFieldArrayName,
    TKeyName
  >(fieldProps);

  const appendWithTrigger = useCallback(
    (
      value:
        | FieldArray<TFieldValues, TFieldArrayName>
        | FieldArray<TFieldValues, TFieldArrayName>[],
      options?: FieldArrayMethodProps,
    ) => {
      append(value, options);
      if (!!parentControlNames && !!parentControlNames.length) {
        trigger(parentControlNames);
      }
    },
    [parentControlNames, append, trigger],
  );

  const removeWithTrigger = useCallback(
    (index?: number | number[]) => {
      remove(index);
      if (!!parentControlNames && !!parentControlNames.length) {
        trigger(parentControlNames);
      }
    },
    [parentControlNames, remove, trigger],
  );

  return {
    append: appendWithTrigger,
    remove: removeWithTrigger,
    ...returnProps,
  };
}

export function useSelectOptions<TFieldValues extends FieldValues>(
  name: string,
  controls: Path<TFieldValues>[],
  options: OptionsOrGroups<SelectOption, SelectGroup>,
) {
  const { getValues, setValue } = useFormContext();

  const values = useWatch<TFieldValues>({ name: controls }) as unknown as (
    | SelectValueType
    | SelectValueType[]
  )[];

  const filteredOptions = useMemo(() => {
    const flattened = values.flat();
    return filterOptions(flattened, options);
  }, [values, options]);

  useEffect(() => {
    const currentValue: SelectValueType = getValues(name);
    const filteredValue = filterValues(currentValue, options);

    if (!areValuesEqual(currentValue, filteredValue)) {
      setValue(name, filteredValue, {
        shouldValidate: true,
      });
    }
  }, [name, values, filteredOptions, getValues, setValue]);

  return filteredOptions;
}
